/**
 * Shared date utilities for consistent timezone-safe date handling across the application.
 * 
 * PROBLEM: JavaScript's Date constructor parses "YYYY-MM-DD" strings as UTC midnight,
 * but when converted back with toISOString().split('T')[0], timezone offsets cause
 * the date to shift by one day.
 * 
 * SOLUTION: Always parse date strings into local date components and create dates
 * using the Date(year, month, day, ...) constructor which uses local timezone.
 */

/**
 * Parse a date string "YYYY-MM-DD" into a local Date object.
 * This avoids the UTC parsing issue with new Date("YYYY-MM-DD").
 * 
 * @param dateStr - Date string in "YYYY-MM-DD" format
 * @param hours - Optional hours (default: 0)
 * @param minutes - Optional minutes (default: 0)
 * @param seconds - Optional seconds (default: 0)
 * @param ms - Optional milliseconds (default: 0)
 * @returns Date object in local timezone
 */
export function parseLocalDate(
  dateStr: string,
  hours: number = 0,
  minutes: number = 0,
  seconds: number = 0,
  ms: number = 0
): Date {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day, hours, minutes, seconds, ms);
}

/**
 * Parse a date and time string into a local Date object.
 * 
 * @param dateStr - Date string in "YYYY-MM-DD" format
 * @param timeStr - Time string in "HH:MM" format
 * @returns Date object in local timezone
 */
export function parseLocalDateTime(dateStr: string, timeStr: string): Date {
  const [year, month, day] = dateStr.split('-').map(Number);
  const [hours, minutes] = timeStr.split(':').map(Number);
  return new Date(year, month - 1, day, hours, minutes, 0, 0);
}

/**
 * Format a Date object to a date input string "YYYY-MM-DD" using local timezone.
 * This should be used instead of toISOString().split('T')[0] which uses UTC.
 * 
 * @param date - Date object
 * @returns Date string in "YYYY-MM-DD" format
 */
export function formatDateInput(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Format a Date object to a time input string "HH:MM" using local timezone.
 * 
 * @param date - Date object
 * @returns Time string in "HH:MM" format
 */
export function formatTimeInput(date: Date): string {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * Create a Date object for the start of a day (midnight) in local timezone.
 * Used for all-day events start time.
 * 
 * @param dateStr - Date string in "YYYY-MM-DD" format
 * @returns Date object at 00:00:00.000 local time
 */
export function createStartOfDay(dateStr: string): Date {
  return parseLocalDate(dateStr, 0, 0, 0, 0);
}

/**
 * Create a Date object for the end of a day in local timezone.
 * Used for all-day events end time.
 * 
 * @param dateStr - Date string in "YYYY-MM-DD" format
 * @returns Date object at 23:59:59.999 local time
 */
export function createEndOfDay(dateStr: string): Date {
  return parseLocalDate(dateStr, 23, 59, 59, 999);
}

/**
 * Safely parse a date from various formats (ISO string, Date object, or date string).
 * Returns a Date object with correct local interpretation.
 * 
 * @param value - Date value (string or Date)
 * @returns Date object
 */
export function safeParseDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  
  if (value instanceof Date) {
    return value;
  }
  
  // If it looks like an ISO string with time (from database), parse normally
  if (value.includes('T')) {
    return new Date(value);
  }
  
  // If it's just a date string "YYYY-MM-DD", parse as local
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return parseLocalDate(value);
  }
  
  // Fallback to standard Date parsing
  return new Date(value);
}

/**
 * Convert a Date object or ISO string to just the date portion for storage.
 * When saving date-only fields to the database, this ensures the date is preserved correctly.
 * 
 * @param date - Date object
 * @returns ISO string representation
 */
export function serializeDate(date: Date): string {
  return date.toISOString();
}

/**
 * Get today's date as a formatted input string.
 * 
 * @returns Today's date in "YYYY-MM-DD" format
 */
export function getTodayDateString(): string {
  return formatDateInput(new Date());
}
